import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as echarts from 'echarts';
import { format, parseISO } from 'date-fns';
import { PowerDataResponse } from '../../../services/powerService';
import { useTheme } from '../../../theme/ThemeContext';
import { useTimezone } from '../../../contexts/useTimezone';

interface PowerDataChartProps {
  powerData: PowerDataResponse;
  dataView?: 'both'; // We only support 'both' now
  hasForecast?: boolean;
}

// Define data categories and their display names
const dataCategories = {
  renewable: {
    title: 'Renewable Energy',
    sources: [
      { key: 'solar', label: 'Solar', color: '#FFCD29', forecastKey: 'solar_forecast' },
      {
        key: 'wind_onshore',
        label: 'Wind Onshore',
        color: '#73C6B6',
        forecastKey: 'wind_onshore_forecast',
      },
      {
        key: 'wind_offshore',
        label: 'Wind Offshore',
        color: '#16A085',
        forecastKey: 'wind_offshore_forecast',
      },
      { key: 'biomass', label: 'Biomass', color: '#A3CB38', forecastKey: 'biomass_forecast' },
      {
        key: 'hydro_run_of_river',
        label: 'Hydro Run of River',
        color: '#3498DB',
        forecastKey: 'hydro_run_of_river_forecast',
      },
      {
        key: 'hydro_water_reservoir',
        label: 'Hydro Reservoir',
        color: '#2980B9',
        forecastKey: 'hydro_water_reservoir_forecast',
      },
      {
        key: 'geothermal',
        label: 'Geothermal',
        color: '#E67E22',
        forecastKey: 'geothermal_forecast',
      },
      {
        key: 'other_renewables',
        label: 'Other Renewables',
        color: '#D4AC0D',
        forecastKey: 'other_renewables_forecast',
      },
    ],
  },
  conventional: {
    title: 'Conventional Energy',
    sources: [
      { key: 'nuclear', label: 'Nuclear', color: '#9B59B6', forecastKey: 'nuclear_forecast' },
      {
        key: 'fossil_brown_coal',
        label: 'Brown Coal',
        color: '#8B5A2B',
        forecastKey: 'fossil_brown_coal_forecast',
      },
      {
        key: 'fossil_hard_coal',
        label: 'Hard Coal',
        color: '#555555',
        forecastKey: 'fossil_hard_coal_forecast',
      },
      { key: 'fossil_gas', label: 'Gas', color: '#E74C3C', forecastKey: 'fossil_gas_forecast' },
      { key: 'fossil_oil', label: 'Oil', color: '#17202A', forecastKey: 'fossil_oil_forecast' },
      { key: 'others', label: 'Others', color: '#7F8C8D', forecastKey: null },
    ],
  },
  consumption: {
    title: 'Consumption & Balance',
    sources: [
      { key: 'load', label: 'Load', color: '#E34290', forecastKey: 'load_forecast' },
      {
        key: 'hydro_pumped_storage_consumption',
        label: 'Pumped Storage Consumption',
        color: '#003366',
        forecastKey: null,
      },
      {
        key: 'cross_border_trading',
        label: 'Cross Border Trading',
        color: '#3C6382',
        forecastKey: null,
      },
      { key: 'import_balance', label: 'Import Balance', color: '#0A3D62', forecastKey: null },
    ],
  },
};

const PowerDataChart: React.FC<PowerDataChartProps> = ({
  powerData,
  dataView = 'both',
  hasForecast = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { theme } = useTheme();
  const { timezone, formatInUserTimezone } = useTimezone();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<'renewable' | 'conventional' | 'all'>('all');

  const updateChart = useCallback(() => {
    if (!chartInstance.current || !powerData || powerData.data.length === 0) return;

    // Get text color based on theme
    const textColor = isDark ? '#e0e0e0' : '#333';
    const backgroundColor = isDark ? '#1F2937' : '#FFFFFF';

    // Transform data for chart
    const timestamps = powerData.data.map((item) => {
      if (timezone === 'UTC') {
        return format(parseISO(item.timestamp), 'yyyy-MM-dd HH:mm');
      } else {
        return formatInUserTimezone(item.timestamp, 'yyyy-MM-dd HH:mm');
      }
    });

    // Helper function to check if a data point has forecast data
    const hasForecastData = (dataPoint: any) => {
      return Object.keys(dataPoint).some(
        (key) => key.includes('forecast') && dataPoint[key] !== null,
      );
    };

    // Find the first index where forecast data starts
    const findForecastStartIndex = () => {
      for (let i = 0; i < powerData.data.length; i++) {
        if (hasForecastData(powerData.data[i])) {
          return i;
        }
      }
      return -1; // No forecast data found
    };

    // Find where forecast data starts (if any)
    const forecastStartIndex = findForecastStartIndex();
    const showForecast = hasForecast; // Always show forecast if available

    // Prepare series data based on active tab
    const seriesData: echarts.SeriesOption[] = [];
    const allSources = [
      ...dataCategories.renewable.sources,
      ...dataCategories.conventional.sources,
    ];

    const visibleSources =
      activeTab === 'all'
        ? allSources
        : activeTab === 'renewable'
          ? dataCategories.renewable.sources
          : dataCategories.conventional.sources;

    // Add actual generation sources
    visibleSources.forEach((source) => {
      const seriesItem: echarts.SeriesOption = {
        name: source.label,
        type: 'line',
        stack: 'Actual',
        areaStyle: {
          color: source.color,
          opacity: 0.8,
        },
        itemStyle: {
          color: source.color,
        },
        emphasis: {
          focus: 'series',
        },
        symbol: 'none',
        lineStyle: {
          width: 0,
        },
        data: powerData.data.map((item, index) => {
          // When we reach forecast data, don't show actual data
          const isBothModeWithForecastData =
            forecastStartIndex !== -1 && index >= forecastStartIndex;

          if (isBothModeWithForecastData) {
            return null;
          }
          return item[source.key as keyof typeof item] || 0;
        }),
      };
      seriesData.push(seriesItem);
    });

    // Add forecast generation sources if available and should be shown
    if (showForecast) {
      visibleSources.forEach((source) => {
        if (!source.forecastKey) return; // Skip sources without forecast

        const forecastSeriesItem: echarts.SeriesOption = {
          name: `${source.label} (Forecast)`,
          type: 'line',
          stack: 'Forecast',
          areaStyle: {
            color: source.color,
            opacity: 0.6, // Lower opacity for forecast data
          },
          itemStyle: {
            color: source.color,
          },
          emphasis: {
            focus: 'series',
          },
          symbol: 'none',
          lineStyle: {
            width: 0,
            type: 'dashed',
          },
          data: powerData.data.map((item, index) => {
            // Only show forecast data where it exists
            return index >= forecastStartIndex
              ? item[source.forecastKey as keyof typeof item] || null
              : null;
          }),
        };

        // Only add the forecast series if it has some data
        if (forecastSeriesItem.data?.some((val) => val !== null)) {
          seriesData.push(forecastSeriesItem);
        }
      });
    }

    // Always add load as a separate line regardless of active tab
    const loadSource = dataCategories.consumption.sources.find((s) => s.key === 'load');
    if (loadSource) {
      // Add actual load
      seriesData.push({
        name: loadSource.label,
        type: 'line',
        stack: undefined, // Don't stack the load line
        itemStyle: {
          color: loadSource.color,
        },
        emphasis: {
          focus: 'series',
        },
        lineStyle: {
          width: 2,
          type: 'solid',
        },
        data: powerData.data.map((item, index) => {
          const isBothModeWithForecastData =
            forecastStartIndex !== -1 && index >= forecastStartIndex;

          if (isBothModeWithForecastData) {
            return null;
          }
          return item.load || 0;
        }),
      });

      // Add forecast load if available
      if (showForecast && loadSource.forecastKey) {
        seriesData.push({
          name: `${loadSource.label} (Forecast)`,
          type: 'line',
          stack: undefined,
          itemStyle: {
            color: loadSource.color,
          },
          emphasis: {
            focus: 'series',
          },
          lineStyle: {
            width: 2,
            type: 'dashed',
          },
          data: powerData.data.map((item, index) => {
            return index >= forecastStartIndex
              ? item[loadSource.forecastKey as keyof typeof item] || null
              : null;
          }),
        });
      }
    }

    // Add a marking line to separate actual and forecast data
    let markLines: any[] = [];
    if (forecastStartIndex >= 0) {
      markLines = [
        {
          symbol: ['none', 'none'],
          lineStyle: {
            color: '#FF6B6B',
            type: 'dashed',
            width: 2,
          },
          label: {
            formatter: 'Forecast Begins',
            position: 'middle',
          },
          xAxis: timestamps[forecastStartIndex],
        },
      ];
    }

    // Chart options
    const option: echarts.EChartsOption = {
      backgroundColor: backgroundColor,
      title: {
        text: `Power Generation Mix - ${powerData.country_code}`,
        subtext: hasForecast ? 'Including forecast data' : undefined,
        left: 'center',
        textStyle: {
          color: textColor,
        },
        subtextStyle: {
          color: isDark ? '#9CA3AF' : '#6B7280',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        formatter: function (params: any) {
          let tooltip = `<div style="margin: 0 0 10px 0;">${params[0].axisValue}</div>`;

          // Group actual and forecast data
          const actualParams = params.filter((p: any) => !p.seriesName.includes('Forecast'));
          const forecastParams = params.filter((p: any) => p.seriesName.includes('Forecast'));

          // Check if this is forecast data - make sure we have actual valid forecast values
          const hasValidForecastData = forecastParams.some(
            (p: any) => p.value !== null && p.value !== undefined,
          );
          if (hasValidForecastData) {
            tooltip += `<div style="color:#FF6B6B;font-weight:bold;margin-bottom:8px;">⚠️ Forecast Data</div>`;
          }

          // Calculate totals
          const actualTotal = actualParams.reduce((sum: number, item: any) => {
            if (!item.seriesName.includes('Load')) {
              // Don't include load in total
              return sum + (item.value || 0);
            }
            return sum;
          }, 0);

          const forecastTotal = forecastParams.reduce((sum: number, item: any) => {
            if (!item.seriesName.includes('Load')) {
              // Don't include load in total
              return sum + (item.value || 0);
            }
            return sum;
          }, 0);

          // Add totals
          if (actualTotal > 0) {
            tooltip += `<div style="margin-bottom: 5px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#E34290;margin-right:5px;"></span>
              <span style="font-weight:bold;">Total Generation: ${Math.round(actualTotal)} MW</span>
            </div>`;
          }

          if (forecastTotal > 0) {
            tooltip += `<div style="margin-bottom: 5px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#E34290;margin-right:5px;"></span>
              <span style="font-weight:bold;">Total Generation (Forecast): ${Math.round(forecastTotal)} MW</span>
            </div>`;
          }

          // Add each actual item
          actualParams.forEach((item: any) => {
            if (item.value !== null && item.value !== undefined) {
              tooltip += `<div style="display:flex;justify-content:space-between;margin: 3px 0;">
                <span style="display:inline-block;margin-right:5px;">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${item.color};margin-right:5px;"></span>
                  ${item.seriesName}:
                </span>
                <span style="float:right;font-weight:bold;">${Math.round(item.value)} MW</span>
              </div>`;
            }
          });

          // Add separator if showing both types
          if (actualParams.length > 0 && forecastParams.length > 0) {
            tooltip += `<hr style="margin: 8px 0; border-color: rgba(255,255,255,0.2);">`;
          }

          // Add each forecast item
          forecastParams.forEach((item: any) => {
            if (item.value !== null && item.value !== undefined) {
              tooltip += `<div style="display:flex;justify-content:space-between;margin: 3px 0;">
                <span style="display:inline-block;margin-right:5px;">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${item.color};margin-right:5px;"></span>
                  ${item.seriesName}:
                </span>
                <span style="float:right;font-weight:bold;">${Math.round(item.value)} MW</span>
              </div>`;
            }
          });

          return tooltip;
        },
      },
      legend: {
        data: seriesData.map((s) => s.name as string),
        bottom: 35,
        type: 'scroll',
        orient: 'horizontal',
        textStyle: {
          color: textColor,
        },
        pageButtonPosition: 'end',
        pageButtonGap: 10,
        tooltip: {
          show: true,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '22%',
        top: '15%',
        containLabel: true,
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          bottom: '2%',
          height: 20,
          borderColor: isDark ? '#374151' : '#E5E7EB',
          textStyle: {
            color: textColor,
          },
          handleStyle: {
            color: isDark ? '#4B5563' : '#9CA3AF',
            borderColor: isDark ? '#6B7280' : '#D1D5DB',
          },
          moveHandleStyle: {
            color: isDark ? '#4B5563' : '#9CA3AF',
          },
          selectedDataBackground: {
            lineStyle: {
              color: isDark ? '#6B7280' : '#D1D5DB',
            },
            areaStyle: {
              color: isDark ? '#4B5563' : '#F3F4F6',
            },
          },
          start: 0,
          end: 100,
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: 0,
          end: 100,
        },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: timestamps,
        axisLabel: {
          color: textColor,
          formatter: (value: string) => {
            // Format the timestamp for better display
            if (timezone === 'UTC') {
              const date = new Date(value);
              return format(date, 'MMM dd, HH:mm');
            } else {
              return formatInUserTimezone(value, 'MMM dd, HH:mm');
            }
          },
        },
        axisLine: {
          lineStyle: {
            color: isDark ? '#4B5563' : '#D1D5DB',
          },
        },
        axisPointer: {
          label: {
            formatter: (params: any) => {
              if (timezone === 'UTC') {
                const date = new Date(params.value);
                return format(date, 'MMM dd, yyyy HH:mm');
              } else {
                return formatInUserTimezone(params.value, 'MMM dd, yyyy HH:mm');
              }
            },
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: isDark ? '#374151' : '#E5E7EB',
            type: 'dashed',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Power (MW)',
        nameTextStyle: {
          color: textColor,
        },
        axisLabel: {
          color: textColor,
          formatter: function (value: number): string {
            if (value >= 1000) {
              return `${value / 1000}k`;
            }
            return value.toString();
          },
        },
        axisLine: {
          lineStyle: {
            color: isDark ? '#4B5563' : '#D1D5DB',
          },
        },
        splitLine: {
          lineStyle: {
            color: isDark ? '#374151' : '#E5E7EB',
            type: 'dashed',
          },
        },
      },
      series: seriesData,
      markLine: {
        silent: true,
        data: markLines,
      },
    };

    chartInstance.current.setOption(option);
  }, [powerData, activeTab, isDark, hasForecast, timezone, formatInUserTimezone]);

  // Initialize or re-initialize chart when necessary
  useEffect(() => {
    if (chartRef.current) {
      // Dispose of the existing chart instance if it exists
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }

      // Create a new chart instance
      chartInstance.current = echarts.init(chartRef.current, isDark ? 'dark' : undefined);

      // Update chart immediately after initialization
      if (powerData && powerData.data.length > 0) {
        updateChart();
      }
    }

    // Handle window resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      chartInstance.current?.dispose();
      window.removeEventListener('resize', handleResize);
    };
  }, [isDark, activeTab, dataView, powerData, updateChart]); // Re-initialize chart when theme, activeTab, or dataView changes

  useEffect(() => {
    if (chartInstance.current && powerData && powerData.data.length > 0) {
      updateChart();
    }
  }, [powerData, dataView, updateChart]);

  const handleTabChange = (tab: 'renewable' | 'conventional' | 'all') => {
    setActiveTab(tab);
  };

  return (
    <div className="mt-6">
      <div className="flex justify-center space-x-4 mb-4">
        <button
          className={`px-4 py-2 rounded-md ${
            activeTab === 'all'
              ? 'bg-[#E34290] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
          onClick={() => handleTabChange('all')}
        >
          All Sources
        </button>
        <button
          className={`px-4 py-2 rounded-md ${
            activeTab === 'renewable'
              ? 'bg-[#16A085] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
          onClick={() => handleTabChange('renewable')}
        >
          Renewable
        </button>
        <button
          className={`px-4 py-2 rounded-md ${
            activeTab === 'conventional'
              ? 'bg-[#E74C3C] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
          onClick={() => handleTabChange('conventional')}
        >
          Conventional
        </button>
      </div>
      <div
        ref={chartRef}
        style={{ width: '100%', height: '500px' }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
      />
    </div>
  );
};

export default PowerDataChart;
